import { useState } from 'react';
import { Loader2, Play, Link2, CheckCircle2, XCircle, Trash2, Eye, Unplug, AlertTriangle, Key, ShieldCheck, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { diagnosticsAPI, openbankingAPI, bridgeAPI, gocardlessAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StatusBadge({ connected, configured, reason }) {
  if (connected) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        Connected
      </Badge>
    );
  }
  // Not-configured is a distinct state from a broken/dead connection --
  // it was never set up (e.g. BH missing client_id), not something that
  // stopped working. Same red destructive badge for both used to make a
  // "never configured" entity look exactly as broken as a real outage.
  if (configured === false) {
    return (
      <Badge className="gap-1 text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300">
        <AlertTriangle className="h-3 w-3" />
        {reason || 'Not configured'}
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      {reason || 'Disconnected'}
    </Badge>
  );
}

const TYPE_LABELS = {
  revolut: 'Revolut Business',
  openbanking: 'Open Banking (TrueLayer)',
  bridge: 'Bridge (BNP)',
  enablebanking: 'Enable Banking (EU)',
  gocardless: 'GoCardless (Bank Account Data)',
  pennylane_bankfeed: 'Pennylane (bank feed)',
  wise: 'Wise',
};

const REASON_LABELS = {
  superseded_duplicate: 'Superseded duplicate',
  sandbox_artifact: 'Sandbox artifact',
  retired_integration: 'Retired integration',
  no_connection_doc: 'No connection doc',
  connected_flag_false: 'Disconnected',
  connected_but_no_token: 'No token stored',
};

function formatReason(reason) {
  if (!reason) return 'Unknown';
  // Handle not_configured:missing_X pattern
  if (reason.startsWith('not_configured:missing_')) {
    const key = reason.replace('not_configured:missing_', '');
    return `Not configured (missing ${key})`;
  }
  return REASON_LABELS[reason] || reason;
}

// Types that support per-connection disconnect
const DISCONNECTABLE_TYPES = new Set(['openbanking', 'bridge', 'gocardless']);

function EntityUnresolvedDetail({ reason }) {
  if (!reason) return null;
  return (
    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md p-2 mt-1 text-[10px] space-y-1">
      <div className="font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        No resolvable entity_id -- Provenance lookup can&apos;t be prefilled for this row. Use the standalone lookup below with an account id instead.
      </div>
      <div>
        Connection doc entity_id: <span className="font-mono">{reason.connection_doc_entity_id ?? 'none'}</span>
      </div>
      {(reason.lookups_tried || []).map((lookup, i) => (
        <div key={i} className="pl-2 border-l border-amber-300 dark:border-amber-700">
          <div className="font-mono">{lookup.lookup}</div>
          {lookup.query && (
            <div>query: <span className="font-mono">{JSON.stringify(lookup.query)}</span></div>
          )}
          {typeof lookup.matched_count === 'number' && (
            <div>matched: {lookup.matched_count}</div>
          )}
          {(lookup.accounts || []).map((a, j) => (
            <div key={j} className="font-mono">
              {a.id ?? a.loser_id}
              {a.entity_id !== undefined && ` entity_id=${a.entity_id ?? 'none'}`}
              {a.hidden !== undefined && ` hidden=${a.hidden}`}
              {a.merged_into !== undefined && a.merged_into !== null && ` merged_into=${a.merged_into}`}
              {a.survivor_found !== undefined && ` survivor_found=${a.survivor_found}`}
              {a.survivor_entity_id !== undefined && ` survivor_entity_id=${a.survivor_entity_id ?? 'none'}`}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ExchangeFailureBanner({ failure }) {
  if (!failure) return null;
  return (
    <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-2 mt-1">
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="h-3 w-3 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="text-[10px] space-y-0.5">
          <div className="font-medium text-red-700 dark:text-red-400">
            Last exchange failure ({failure.stage === 'client_assertion_build' ? 'JWT build' : 'HTTP exchange'})
          </div>
          {failure.http_status && (
            <div>HTTP {failure.http_status}</div>
          )}
          <div className="font-mono">
            {failure.error}{failure.error_description ? `: ${failure.error_description}` : ''}
          </div>
          {failure.timestamp && (
            <div className="text-muted-foreground">{new Date(failure.timestamp).toLocaleString()}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function IntegrationConnectionStatePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [staleLoading, setStaleLoading] = useState(false);
  const [staleCandidates, setStaleCandidates] = useState(null);
  const [applying, setApplying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  // Config parity
  const [parityLoading, setParityLoading] = useState(false);
  const [parityResult, setParityResult] = useState(null);
  // Self-check
  const [selfCheckLoading, setSelfCheckLoading] = useState(null);
  const [selfCheckResults, setSelfCheckResults] = useState({});
  // Account transaction provenance -- standalone front door, robust to a
  // row that can't resolve an entity_id at all (entity_unresolved above):
  // the operator can still look up ANY account directly, by account id or
  // entity id.
  const [provenanceLookupType, setProvenanceLookupType] = useState('entity_id');
  const [provenanceQuery, setProvenanceQuery] = useState('');
  const [provenanceLoading, setProvenanceLoading] = useState(false);
  const [provenanceResult, setProvenanceResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.integrationConnectionState();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const disconnectConnection = async (integrationId, integrationType) => {
    setDisconnecting(integrationId);
    try {
      if (integrationType === 'openbanking') {
        await openbankingAPI.disconnect(integrationId);
      } else if (integrationType === 'bridge') {
        await bridgeAPI.disconnect(integrationId);
      } else if (integrationType === 'gocardless') {
        await gocardlessAPI.disconnect(integrationId);
      }
      toast.success(`Disconnected ${integrationId} (marked superseded)`);
      // Refresh the state table
      run();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDisconnecting(null);
    }
  };

  const previewStale = async () => {
    setStaleLoading(true);
    setStaleCandidates(null);
    try {
      const { data } = await diagnosticsAPI.staleConnectionsPreview();
      setStaleCandidates(data);
      if (data.count === 0) {
        toast.info('No stale connection docs found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStaleLoading(false);
    }
  };

  const applyStale = async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.staleConnectionsApply();
      toast.success(`Marked ${data.marked} doc(s) as superseded/archived`);
      setStaleCandidates(null);
      // Refresh the main state
      run();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const loadParity = async () => {
    setParityLoading(true);
    setParityResult(null);
    try {
      const { data } = await diagnosticsAPI.revolutExchangeConfig();
      setParityResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setParityLoading(false);
    }
  };

  const runSelfCheck = async (connectionId) => {
    setSelfCheckLoading(connectionId);
    try {
      const { data } = await diagnosticsAPI.revolutExchangeSelfCheck(connectionId);
      setSelfCheckResults(prev => ({ ...prev, [connectionId]: data }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSelfCheckLoading(null);
    }
  };

  // rowEntityId: prefill from a row's "Provenance" button (always an
  // entity_id lookup). With no argument, uses the standalone front door's
  // own type toggle + input -- account id or entity id, independent of
  // whether any row above could resolve one at all.
  const runProvenance = async (rowEntityId) => {
    let params;
    if (rowEntityId) {
      params = { entity_id: rowEntityId };
      setProvenanceLookupType('entity_id');
      setProvenanceQuery(rowEntityId);
    } else {
      const value = provenanceQuery.trim();
      if (!value) {
        toast.error(provenanceLookupType === 'entity_id' ? 'Enter an entity_id to look up' : 'Enter an account id to look up');
        return;
      }
      params = { [provenanceLookupType]: value };
    }
    setProvenanceLoading(true);
    setProvenanceResult(null);
    try {
      const { data } = await diagnosticsAPI.accountTransactionProvenance(params);
      setProvenanceResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setProvenanceLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Integration Connection State
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Canonical connection state for every bank integration. Shows the stored evidence (field names, timestamps, masked ids) and which checks pass or fail. Read-only.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Summary banner */}
          <div className={`border rounded-lg p-3 ${result.all_connected ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.all_connected ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">All integrations connected ({result.connected_count}/{result.total_count})</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">{result.connected_count} of {result.total_count} integrations connected</span>
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-1.5 font-medium">Integration</th>
                  <th className="text-left px-3 py-1.5 font-medium">Type</th>
                  <th className="text-left px-3 py-1.5 font-medium">Status</th>
                  <th className="text-left px-3 py-1.5 font-medium">Connected At</th>
                  <th className="text-left px-3 py-1.5 font-medium">Token Expiry</th>
                  <th className="text-left px-3 py-1.5 font-medium">Last Synced</th>
                  <th className="text-left px-3 py-1.5 font-medium">Stored Fields</th>
                  <th className="text-left px-3 py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {(result.states || []).map((s) => (
                  <tr key={`${s.integration_type}-${s.integration_id}`} className="border-b last:border-0">
                    <td className="px-3 py-1.5 font-mono font-medium">
                      <div>
                        {s.label || s.integration_id}
                        {s.entity_id && (
                          <span className="text-muted-foreground ml-1">({s.entity_id})</span>
                        )}
                      </div>
                      {s.last_exchange_failure && (
                        <ExchangeFailureBanner failure={s.last_exchange_failure} />
                      )}
                      {!s.entity_id && s.entity_unresolved && (
                        <EntityUnresolvedDetail reason={s.entity_unresolved} />
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {TYPE_LABELS[s.integration_type] || s.integration_type}
                    </td>
                    <td className="px-3 py-1.5">
                      <StatusBadge connected={s.connected} configured={s.configured} reason={formatReason(s.reason)} />
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      {s.connected_at ? new Date(s.connected_at).toLocaleString() : '--'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      {s.token_expiry ? new Date(s.token_expiry).toLocaleString() : '--'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      {s.last_synced || '--'}
                    </td>
                    <td className="px-3 py-1.5 text-[10px] text-muted-foreground max-w-[200px] truncate" title={(s.raw_fields_present || []).join(', ')}>
                      {(s.raw_fields_present || []).join(', ') || '--'}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-1">
                        {s.entity_id && (
                          <Button
                            onClick={() => runProvenance(s.entity_id)}
                            disabled={provenanceLoading}
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            title="Show transaction provenance for this entity"
                          >
                            <Search className="h-3 w-3 mr-1" />
                            Provenance
                          </Button>
                        )}
                        {s.connected && DISCONNECTABLE_TYPES.has(s.integration_type) && (
                          <Button
                            onClick={() => disconnectConnection(s.integration_id, s.integration_type)}
                            disabled={disconnecting === s.integration_id}
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[10px]"
                          >
                            {disconnecting === s.integration_id ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Unplug className="h-3 w-3 mr-1" />
                            )}
                            Disconnect
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Account transaction provenance */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                <Search className="h-4 w-4 text-purple-600" />
                Account Transaction Provenance
              </h4>
            </div>
            <p className="text-[10px] text-muted-foreground mb-2">
              For a given entity_id or bank_accounts.id, distinct source/feed values on its transactions
              in the last 30 days, with counts and latest timestamp per source. Runs against production by
              definition (reads db.transactions on the live backend) -- this is how a multi-feed account
              (e.g. a dead Bridge/BNP row alongside a live Pennylane bank feed) gets a truthful answer to
              "which feed actually wrote the recent rows," instead of inferring it from one connection's
              own stale sync timestamp. Click "Provenance" on a row above to prefill by entity_id, or use
              this standalone lookup directly -- by account id or entity id -- for any account, including
              one no row above could resolve at all.
            </p>
            <div className="flex gap-2 mb-2 items-center">
              <div className="flex border rounded-md overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setProvenanceLookupType('entity_id')}
                  className={`px-2 py-1.5 ${provenanceLookupType === 'entity_id' ? 'bg-primary text-primary-foreground' : 'bg-transparent'}`}
                  data-testid="provenance-lookup-type-entity"
                >
                  Entity ID
                </button>
                <button
                  type="button"
                  onClick={() => setProvenanceLookupType('bank_account_id')}
                  className={`px-2 py-1.5 ${provenanceLookupType === 'bank_account_id' ? 'bg-primary text-primary-foreground' : 'bg-transparent'}`}
                  data-testid="provenance-lookup-type-account"
                >
                  Account ID
                </button>
              </div>
              <Input
                value={provenanceQuery}
                onChange={(e) => setProvenanceQuery(e.target.value)}
                placeholder={provenanceLookupType === 'entity_id' ? 'entity_id, e.g. sci_113' : 'bank_accounts.id, e.g. bridge_acct_1'}
                className="h-8 text-xs max-w-[240px]"
                data-testid="provenance-standalone-input"
              />
              <Button onClick={() => runProvenance()} disabled={provenanceLoading} size="sm" variant="outline" data-testid="provenance-standalone-check">
                {provenanceLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Search className="h-3 w-3 mr-1" />
                )}
                Check
              </Button>
            </div>
            {provenanceResult && provenanceResult.lookup_evidence && (
              <div className="text-[10px] mb-2" data-testid="provenance-lookup-evidence">
                {provenanceResult.bank_account_id && (
                  provenanceResult.lookup_evidence.bank_account_found ? (
                    <span className="text-muted-foreground">
                      Account found (entity_id: <span className="font-mono">{provenanceResult.lookup_evidence.bank_account_entity_id ?? 'none'}</span>,
                      {' '}hidden: {String(provenanceResult.lookup_evidence.bank_account_hidden)},
                      {' '}source: <span className="font-mono">{provenanceResult.lookup_evidence.bank_account_source ?? 'none'}</span>)
                    </span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      No bank_accounts document with this id -- likely a typo, not "zero transactions."
                    </span>
                  )
                )}
                {provenanceResult.entity_id && (
                  provenanceResult.lookup_evidence.entity_id_known ? (
                    <span className="text-muted-foreground">Known entity_id (in the canonical registry).</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      entity_id not in the canonical registry -- likely a typo, not "zero transactions."
                    </span>
                  )
                )}
              </div>
            )}
            {provenanceResult && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-1.5 font-medium">Source</th>
                      <th className="text-left px-3 py-1.5 font-medium">Count ({provenanceResult.window_days}d)</th>
                      <th className="text-left px-3 py-1.5 font-medium">Latest Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(provenanceResult.sources || []).map((s) => (
                      <tr key={s.source} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-mono font-medium">{s.source}</td>
                        <td className="px-3 py-1.5">{s.count}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">
                          {s.latest_timestamp ? new Date(s.latest_timestamp).toLocaleString() : '--'}
                        </td>
                      </tr>
                    ))}
                    {(!provenanceResult.sources || provenanceResult.sources.length === 0) && (
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-center text-muted-foreground">
                          No transactions in the last {provenanceResult.window_days} days for this entity.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Revolut exchange config parity */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                <Key className="h-4 w-4 text-amber-600" />
                Revolut Exchange Config Check
              </h4>
              <Button
                onClick={loadParity}
                disabled={parityLoading}
                size="sm"
                variant="outline"
              >
                {parityLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Eye className="h-3 w-3 mr-1" />
                )}
                Load
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mb-2">
              Structural fingerprints for each Revolut entity (client_id prefix, key SHA-256, JWT claims, key source). Secrets never shown. Compare bi_uk against siblings to spot divergence.
            </p>

            {parityResult && (
              <div className="space-y-3">
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-1.5 font-medium">Entity</th>
                        <th className="text-left px-3 py-1.5 font-medium">Client ID</th>
                        <th className="text-left px-3 py-1.5 font-medium">Key Source</th>
                        <th className="text-left px-3 py-1.5 font-medium">Key Fingerprint</th>
                        <th className="text-left px-3 py-1.5 font-medium">Key Bits</th>
                        <th className="text-left px-3 py-1.5 font-medium">JWT iss</th>
                        <th className="text-left px-3 py-1.5 font-medium">JWT aud</th>
                        <th className="text-left px-3 py-1.5 font-medium">Self-Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(parityResult.entities || []).map((e) => {
                        const sc = selfCheckResults[e.connection_id];
                        return (
                          <tr key={e.connection_id} className="border-b last:border-0">
                            <td className="px-3 py-1.5 font-mono font-medium">
                              {e.label}
                              <span className="text-muted-foreground ml-1">({e.connection_id})</span>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{e.client_id_fingerprint}</td>
                            <td className="px-3 py-1.5 text-[10px]">
                              <Badge variant={e.key_source === 'per_account' ? 'default' : e.key_source === 'default' ? 'secondary' : 'destructive'} className="text-[10px]">
                                {e.key_source}
                              </Badge>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{e.key_fingerprint}</td>
                            <td className="px-3 py-1.5 text-[10px]">{e.key_bits ?? '--'}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{e.jwt_claims?.iss || '--'}</td>
                            <td className="px-3 py-1.5 font-mono text-[10px]">{e.jwt_claims?.aud || '--'}</td>
                            <td className="px-3 py-1.5">
                              {sc ? (
                                <Badge className={`text-[10px] ${sc.all_pass ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                                  {sc.all_pass ? (
                                    <><CheckCircle2 className="h-3 w-3 mr-0.5" /> Pass</>
                                  ) : (
                                    <><XCircle className="h-3 w-3 mr-0.5" /> Fail</>
                                  )}
                                </Badge>
                              ) : (
                                <Button
                                  onClick={() => runSelfCheck(e.connection_id)}
                                  disabled={selfCheckLoading === e.connection_id}
                                  size="sm"
                                  variant="outline"
                                  className="h-5 px-1.5 text-[10px]"
                                >
                                  {selfCheckLoading === e.connection_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <><ShieldCheck className="h-3 w-3 mr-0.5" /> Check</>
                                  )}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Self-check detail panels */}
                {Object.entries(selfCheckResults).map(([connId, sc]) => (
                  <div key={connId} className={`border rounded-lg p-3 ${sc.all_pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {sc.all_pass ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className="text-xs font-medium">Self-check: {connId}</span>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(sc.checks || {}).map(([name, check]) => (
                        <div key={name} className="flex items-center gap-2 text-[10px]">
                          {check.pass ? (
                            <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                          )}
                          <span className="font-mono">{name}</span>
                          <span className="text-muted-foreground">{check.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stale cleanup */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Stale Connection Cleanup</h4>
              <Button
                onClick={previewStale}
                disabled={staleLoading}
                size="sm"
                variant="outline"
              >
                {staleLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Eye className="h-3 w-3 mr-1" />
                )}
                Preview
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mb-2">
              Lists superseded duplicates, sandbox artifacts, and retired integration docs. Apply marks them superseded (never deletes).
            </p>

            {staleCandidates && staleCandidates.count > 0 && (
              <>
                <div className="overflow-x-auto border rounded-lg mb-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-1.5 font-medium">Collection</th>
                        <th className="text-left px-3 py-1.5 font-medium">Connection ID</th>
                        <th className="text-left px-3 py-1.5 font-medium">Institution</th>
                        <th className="text-left px-3 py-1.5 font-medium">Connected</th>
                        <th className="text-left px-3 py-1.5 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staleCandidates.candidates.map((c, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-1.5 font-mono text-[10px]">{c.collection}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px]">{c.connection_id}</td>
                          <td className="px-3 py-1.5">
                            {c.institution_name || c.institution_id}
                          </td>
                          <td className="px-3 py-1.5">
                            {c.connected ? (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">Yes</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px]">No</Badge>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {REASON_LABELS[c.reason] || c.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button
                  onClick={applyStale}
                  disabled={applying}
                  size="sm"
                  variant="destructive"
                >
                  {applying ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-1" />
                  )}
                  Apply ({staleCandidates.count} doc{staleCandidates.count !== 1 ? 's' : ''})
                </Button>
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
